import { Crown, Check, ArrowRight, Sparkles, Calendar, Shield, LogIn } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface TrialInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const trialFeatures = [
  'All Pro features unlocked',
  '25 free generations (up to 2 per day)',
  'All 4 social platforms',
  '3 post variations per platform',
  '8 workspaces',
  'AI image generation',
  'Company Intelligence',
  'No credit card required',
];

export function TrialInfoModal({ isOpen, onClose }: TrialInfoModalProps) {
  const navigate = useNavigate();

  const handleSignUp = () => {
    onClose();
    navigate('/register');
  };

  const handleLogin = () => {
    onClose();
    navigate('/login');
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <DialogHeader className="text-center pb-1">
          <div className="mx-auto w-12 h-12 rounded-2xl bg-cargo flex items-center justify-center mb-2">
            <Crown className="w-6 h-6 text-cargo-ink" />
          </div>
          <DialogTitle className="text-xl font-bold">
            Start Your 15-Day Free Trial
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Trial highlight */}
          <div className="bg-paper rounded-xl p-3 border border-hairline">
            <div className="flex items-center gap-3 mb-1">
              <Sparkles className="w-5 h-5 text-cargo" />
              <span className="font-semibold text-ink">
                Full Pro Access
              </span>
            </div>
            <p className="text-sm text-steel-dark">
              Experience all premium features free for 15 days. Create psychology-powered posts that actually convert.
            </p>
          </div>

          {/* Features list */}
          <div>
            <h4 className="text-sm font-semibold text-muted-foreground mb-2">
              WHAT'S INCLUDED
            </h4>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5">
              {trialFeatures.map((feature) => (
                <li key={feature} className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-ink/10 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-ink" />
                  </div>
                  <span className="text-sm text-foreground">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* What happens after */}
          <div className="bg-paper rounded-xl p-3">
            <div className="flex items-center gap-3 mb-1">
              <Calendar className="w-5 h-5 text-muted-foreground" />
              <span className="font-medium text-foreground">After 15 days</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Choose a plan that fits your needs. Plans start at just $10/month. You'll be prompted to select a tier to continue using Zupa.
            </p>
          </div>

          {/* Security note */}
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              No credit card required to start. Cancel anytime during your trial with no charges.
            </span>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col gap-2 pt-1">
          <Button
            onClick={handleSignUp}
            className="w-full bg-cargo hover:opacity-90 text-cargo-ink"
            size="lg"
          >
            Create Free Account
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>

          {/* Already have account option */}
          <Button
            variant="outline"
            onClick={handleLogin}
            className="w-full"
          >
            <LogIn className="w-4 h-4 mr-2" />
            Already have an account? Log in
          </Button>

          <Button
            variant="ghost"
            onClick={onClose}
            className="w-full h-8 text-muted-foreground text-sm"
          >
            Maybe later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
